import type {
  InvoiceSearchResult,
  Pagination,
  Transaction,
} from "@/apis/gql/types";
import { useTenant } from "@/providers/session";
import { gql, useQuery } from "@apollo/client";
import { useCallback, useEffect, useState } from "react";

export const useSearchTransactionInvoices = ({
  transactions,
  page,
  limit,
  offset,
  global,
}: {
  transactions: Transaction[];
  page: number;
  limit: number;
  offset: number;
  global: string;
}) => {
  const { tenantId } = useTenant();
  const [hasNextPage, setHasNextPage] = useState(true);
  const [pagination, setPagination] = useState<Pagination>({
    currentPage: page,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: limit,
    hasNextPage: false,
    hasPreviousPage: false,
    nextPage: page,
    previousPage: page > 1 ? page - 1 : 1,
  });

  // Normalize comma decimal separator to dot and remove negative sign for amount searches
  const normalizedGlobal = global.replace(/,/g, ".").replace(/^-/, "");

  const { data, loading, error, fetchMore } = useQuery<{
    searchTransactionInvoices: {
      data: InvoiceSearchResult[];
      pagination: Pagination;
    };
  }>(SEARCH_TRANSACTION_INVOICES, {
    variables: {
      tenantId,
      transactions: transactions.map((transaction) => ({
        bankTxnId: transaction.bankTxnId,
        bookingDate: transaction.bookingDate,
        amount: transaction.amount,
        counterpartyName: transaction.counterpartyName,
      })),
      page,
      limit,
      offset,
      global: normalizedGlobal,
    },
    skip: transactions.length === 0,
  });

  useEffect(() => {
    if (data?.searchTransactionInvoices?.pagination && !loading) {
      setPagination(data.searchTransactionInvoices.pagination);
    }
  }, [data?.searchTransactionInvoices?.pagination, loading]);

  const loadMore = useCallback(async () => {
    if (!hasNextPage || loading) return;

    const currentTransactions = data?.searchTransactionInvoices?.data || [];
    const currentPagination = data?.searchTransactionInvoices?.pagination;

    if (!currentPagination?.hasNextPage) {
      setHasNextPage(false);
      return;
    }

    try {
      await fetchMore({
        variables: {
          page: currentPagination.nextPage,
          offset: currentTransactions.length,
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;

          const newTransactions =
            fetchMoreResult.searchTransactionInvoices.data;
          const newPagination =
            fetchMoreResult.searchTransactionInvoices.pagination;

          setHasNextPage(newPagination.hasNextPage);

          return {
            ...prev,
            searchTransactionInvoices: {
              ...prev.searchTransactionInvoices,
              data: [...currentTransactions, ...newTransactions],
              pagination: newPagination,
            },
          };
        },
      });
    } catch (err) {
      console.error("Error loading more transactions:", err);
    }
  }, [data, fetchMore, hasNextPage, loading]);

  return {
    data: data?.searchTransactionInvoices?.data,
    pagination,
    loading,
    error,
    loadMore,
    hasNextPage,
  };
};

export const SEARCH_TRANSACTION_INVOICES = gql`
  query SearchTransactionInvoices(
    $tenantId: String!
    $transactions: [SearchInvoicesTransactionsInput!]
    $page: Int
    $limit: Int
    $offset: Int
    $query: JSON
    $global: String
  ) {
    searchTransactionInvoices(
      tenantId: $tenantId
      transactions: $transactions
      page: $page
      limit: $limit
      offset: $offset
      query: $query
      global: $global
    ) {
      data {
        invoiceId
        totalAmount
        invoiceDate
        invoiceNo
        supplierName
        createdAt
        currency
        status
        invoiceType
        transactions {
          bankTransactionId
          amount
          counterpartyName
          counterpartyIban
          purpose
          currency
          side
          bookingDate
        }
        journalEntries {
          journalEntryId
          journalLines {
            journalLineId
            debit
            credit
            currency
            originalCurrency
            originalAmount
          }
        }
      }
      pagination {
        currentPage
        totalPages
        totalItems
        itemsPerPage
        hasNextPage
        hasPreviousPage
        nextPage
        previousPage
      }
    }
  }
`;
